// brainRouter — resolve o "cérebro" (LLM) de um dispatch num chatFn pro
// runLocalAgentLoop. Cloud (default) → Worker /agent-turn (chave server-side,
// reusa o endpoint existente); local → IPC Ollama/LM Studio.
//
// Extraído da lógica que já existia em SessionWorkspace.jsx (cloud vs local),
// pra ser reusado pelo runner remoto (useRemoteRunner) — ver DECISIONS D-V2-002.
// A execução das tools continua SEMPRE local no Mac (via runLocalAgentLoop);
// o "cérebro" é que pode ser cloud, tornando o Ollama opcional.

import { apiFetch, apiUrl, getStoredToken } from './api.js';
import { runCloudTurn } from './cloudStream.js';
import { readFlagDefaultOn, readFlagDefaultOff } from './flags/sprintFlags.js';

// E3-WS2-T02 — keyless-resilient default. Must be registry-resolvable (findModel)
// AND backed by an account key. google/gemini-2.5-flash matches useAgentStore.
// DEFAULT_MODEL (switched 2026-06-15: cheaper + more reliable tool-caller than the
// previous deepseek/deepseek-v4-pro). Quem quiser Claude usa OpenRouter (proxia).
export const DEFAULT_CLOUD_MODEL = 'google/gemini-2.5-flash';

// harness-library-uplift E6-WS2 — brain de dispatch capaz de tools. No smoke ao vivo
// (13 jul) o gemini-2.5-flash loopou em read_file com args VAZIOS (guards seguraram
// como tool_repeat_loop, mas o dispatch falhou). kimi-k2.5 é tool-caller forte.
// FLIP default-ON (E6-LV 14 jul): dispatch headless real com brain kimi completou
// tarefa de 2 tools no .app (dispatch 363b4ef1). Kill-switch:
// apolo_dispatch_brain_kimi='0'/'false' ⇒ volta pro gemini. `context.brain`
// explícito SEMPRE vence (precedência preservada — só muda o fallback).
export const KIMI_DISPATCH_MODEL = 'kimi/kimi-k2.5';
function resolveDefaultCloudModel() {
  return readFlagDefaultOn('apolo_dispatch_brain_kimi') ? KIMI_DISPATCH_MODEL : DEFAULT_CLOUD_MODEL;
}

// Cloud: um turno via Worker (resolve adapter+key e devolve tool_calls
// normalizados). O handler ignora o :id, então usamos o dispatchId só como
// container lógico.
//
// 2026-08-21 — STREAMING (Pedro, dogfood do harness). Antes este caminho era
// NON-streaming: ignorava `onDelta`, então o idle-timer do runLocalAgentLoop
// (agentLoop.callWithTimeout, LLM_CALL_TIMEOUT_MS=90s) NUNCA recebia um bump e
// disparava em 90s em QUALQUER chamada mais lenta que isso — mesmo saudável. O
// dogfood provou ao vivo: uma geração longa (skill design-2 pesada → prompt
// gigante → GLM-5.2 raciocinando >90s) morria com stopReason `llm_timeout`, o
// 3º clone do mesmo bug (os 2 primeiros eram no Worker). Agora streama igual ao
// composer (buildChatFn.js) via runCloudTurn: cada delta reseta o idle-timer, e o
// idle-timeout do Worker protege o upstream. `nonStreamCall` continua como
// HARD-fallback provado (sem duplo-cobrar).
function buildCloudChatFn(model, llmAdapterId, dispatchId) {
  const id = dispatchId || 'remote';
  const turnBody = (messages, tools) => ({ messages, tools, model, llm_adapter_id: llmAdapterId || null });
  return async ({ messages, tools }, onDelta, signal) => {
    const streamFetch = (sig) => {
      const token = getStoredToken();
      return fetch(apiUrl(`/api/code-ide/sessions/${id}/agent-turn?stream=1`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(turnBody(messages, tools)),
        signal: sig,
      });
    };
    const nonStreamCall = async (sig) => {
      // E3: AbortSignal repassado pro fetch → cancela a chamada cloud em voo no Stop.
      const res = await apiFetch(`/api/code-ide/sessions/${id}/agent-turn`, {
        method: 'POST',
        body: turnBody(messages, tools),
        signal: sig,
      });
      if (!res.ok) return { ok: false, error: res.error || 'agent-turn falhou' };
      return { ok: true, content: res.data?.content || '', tool_calls: res.data?.tool_calls || [] };
    };
    return runCloudTurn({ streamFetch, nonStreamCall, onDelta, signal });
  };
}

// Local: IPC direto pro provider rodando na máquina.
function buildLocalChatFn(isLm) {
  return (params) => {
    const ipc = isLm ? window.apolo?.lmstudio?.chat : window.apolo?.ollama?.chat;
    if (typeof ipc !== 'function') {
      return Promise.resolve({ ok: false, error: `IPC ${isLm ? 'LM Studio' : 'Ollama'} indisponível` });
    }
    return ipc(params);
  };
}

/**
 * resolveBrain(brain, { dispatchId }) → { kind, chatFn, baseUrl, model }
 * `brain` = { kind:'cloud'|'local', model?, adapterId?, baseUrl?, provider? }.
 * Ausente → cloud default (só o Apolo aberto basta, sem Ollama).
 */
export function resolveBrain(brain, opts = {}) {
  const kind = brain?.kind === 'local' ? 'local' : 'cloud';

  if (kind === 'local') {
    const isLm =
      String(brain.baseUrl || '').includes('1234') || /lm\s?studio/i.test(brain.provider || '');
    // The `ollama/`/`lmstudio/` prefix is a ROUTING tag (it's how appExecutionAdapter
    // and isLocalAdapter decide a model is local) — it is NOT part of the model's
    // real name. Ollama's /api/chat and LM Studio's /v1 expect the BARE name
    // (`qwen2.5-hermes:latest`), so strip the prefix here, at the boundary that
    // talks to the local provider. Without this, a local agent set to
    // `ollama/<model>` routes local correctly but then 404s ("model not found")
    // because the provider never knew the prefix.
    const rawModel = brain.model || (isLm ? '' : 'qwen2.5:7b');
    const model = String(rawModel).replace(/^(ollama|lmstudio|lm-?studio)\//i, '');
    return {
      kind: 'local',
      chatFn: buildLocalChatFn(isLm),
      baseUrl: brain.baseUrl || (isLm ? 'http://localhost:1234/v1' : 'http://127.0.0.1:11434'),
      model,
    };
  }

  // E6: brain?.model explícito vence; só o FALLBACK é gated (kimi com a flag ON).
  const model = brain?.model || resolveDefaultCloudModel();
  return {
    kind: 'cloud',
    chatFn: buildCloudChatFn(model, brain?.adapterId, opts.dispatchId),
    baseUrl: undefined,
    model,
  };
}

// E2 (System One model routing) — registry-safe default tier map. opts.tiers overrides.
const DEFAULT_ROUTE_TIERS = Object.freeze({
  cheap: DEFAULT_CLOUD_MODEL,     // google/gemini-2.5-flash
  standard: KIMI_DISPATCH_MODEL,  // kimi/kimi-k2.5 (strong tool-caller)
  frontier: KIMI_DISPATCH_MODEL,  // conservative; override via opts.tiers for a bigger model
});

/**
 * resolveBrain + optional calibrated model routing (E2). ADDITIVE + FAIL-CLOSED:
 * with the flag off, a local brain, an explicit model, or no message, it is
 * exactly resolveBrain(). Only when `apolo_so_route` is on AND the caller left
 * the cloud model to the fallback does it ask the Worker for a tier.
 * @param {object} brain
 * @param {string} userMessage
 * @param {object} [opts] { dispatchId, tiers }
 * @param {Function} [resolve] the resolver to delegate to (DI; defaults to resolveBrain)
 * @returns {Promise<{kind:string, chatFn:Function, baseUrl:(string|undefined), model:string}>}
 */
export async function resolveBrainRouted(brain, userMessage, opts = {}, resolve = resolveBrain) {
  const canRoute =
    readFlagDefaultOff('apolo_so_route') &&
    brain?.kind !== 'local' &&
    !brain?.model &&
    typeof userMessage === 'string' &&
    userMessage.trim().length > 0;
  if (!canRoute) return resolve(brain, opts);
  try {
    const { routeModel } = await import('./systemOneClient.js');
    const routed = await routeModel(userMessage, opts.tiers || DEFAULT_ROUTE_TIERS);
    if (routed?.model) return resolve({ ...brain, model: routed.model }, opts);
  } catch {
    /* fail-closed: fall back to the default brain */
  }
  return resolve(brain, opts);
}
